// oxlint-disable-next-line no-restricted-imports
import { Select as MantineSelect } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { fdsCssVariables } from "#app/theme/v2/cssVariablesResolver";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import { getInputErrorBackgroundColor } from "#app/theme/v2/inputStates";
import { lineHeights } from "#app/theme/v2/lineHeights";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { DROPDOWN_Z_INDEX } from "#app/theme/v2/zIndex";

import classes from "./Select.module.css";

type SelectItem = { value: string; label: string };
type SelectGroup = { group: string; items: SelectItem[] };

type SelectProps = {
  label?: string;
  placeholder?: string;
  data: SelectItem[] | string[] | SelectGroup[];
  error?: string | boolean;
  disabled?: boolean;
  required?: boolean;
  value?: string | null;
  onChange?: (value: string | null) => void;
  searchable?: boolean;
  clearable?: boolean;
  nothingFoundMessage?: string;
  renderOption?: React.ComponentProps<typeof MantineSelect>["renderOption"];
  renderValue?: React.ReactNode;
  dropdownOpened?: boolean;
  onDropdownOpen?: () => void;
  onDropdownClose?: () => void;
  onOptionSubmit?: (value: string) => void;
  "aria-label"?: string;
};

const Select = ({
  label,
  placeholder,
  data,
  error,
  disabled,
  required,
  value,
  onChange,
  searchable = true,
  clearable,
  nothingFoundMessage,
  renderOption,
  renderValue,
  dropdownOpened,
  onDropdownOpen,
  onDropdownClose,
  onOptionSubmit,
  "aria-label": ariaLabel,
}: SelectProps) => {
  const { t } = useTranslation("components");
  const hasValue = Boolean(value);
  const hasCustomValue = Boolean(hasValue && renderValue);

  return (
    <MantineSelect
      label={label}
      aria-label={ariaLabel}
      placeholder={placeholder ?? t("select.placeholder")}
      data={data}
      w="100%"
      error={error}
      disabled={disabled}
      required={required}
      value={value}
      onChange={onChange}
      searchable={searchable}
      clearable={clearable}
      renderOption={renderOption}
      comboboxProps={{ zIndex: DROPDOWN_Z_INDEX }}
      dropdownOpened={dropdownOpened}
      onDropdownOpen={onDropdownOpen}
      onDropdownClose={onDropdownClose}
      onOptionSubmit={onOptionSubmit}
      leftSection={hasCustomValue ? renderValue : undefined}
      leftSectionWidth={hasCustomValue ? `calc(100% - ${spacing[9]})` : undefined}
      leftSectionPointerEvents="none"
      leftSectionProps={
        hasCustomValue
          ? {
              className: classes.customValueSection,
            }
          : undefined
      }
      nothingFoundMessage={nothingFoundMessage ?? t("select.nothingFound")}
      rightSection={<IconChevronDown size={12} color={colors.icon.primaryDefault} />}
      rightSectionPointerEvents="none"
      classNames={{
        label: classes.label,
        input: hasCustomValue ? `${classes.input!} ${classes.inputCustomValue!}` : classes.input,
        dropdown: classes.dropdown,
        option: classes.option,
        groupLabel: classes.groupLabel,
      }}
      styles={{ dropdown: fdsCssVariables }}
      style={
        {
          "--select-padding": `${spacing[2]} ${spacing[9]} ${spacing[2]} ${spacing[2]}`,
          "--select-label-fz": fontSizes[14],
          "--select-label-fw": fontWeights.medium,
          "--select-label-lh": lineHeights[1.25],
          "--select-label-c": colors.text.primaryDefault,
          "--select-label-mb": spacing[1],
          "--select-input-fz": fontSizes[14],
          "--select-input-fw": fontWeights.regular,
          "--select-input-h": spacing[9],
          "--select-input-radius": radius.xs,
          "--select-input-bg": colors.bg.elevation.primary,
          "--select-input-bd": colors.border.tertiary,
          "--select-input-c": colors.text.primaryDefault,
          "--select-placeholder-c": colors.text.secondaryDefault,
          "--select-focus-bd": colors.border.primaryDefault,
          "--select-error-bd": colors.border.error.primary,
          "--select-error-bg": getInputErrorBackgroundColor({ hasValue }),
          "--select-disabled-bg": colors.bg.elevation.tertiary,
          "--select-disabled-c": colors.text.primaryDisabled,
        } as React.CSSProperties
      }
    />
  );
};

export { Select };
